import type { ReactElement } from 'react';
import type { LivingBriefDetail, ProposalBuildPage, ProposalBuildState } from '@shared/api';
import type { Proposal } from '@shared/types';
import { isBuildInFlight } from '../../lib/buildRail';
import { PageGrid } from './PageGrid';
import { StageTracker, startingStageTrackerState } from './StageTracker';
import './live-build.css';

/**
 * D · Live build (docs/live-build.md §5). Covers `awaiting_confirmation`
 * (planned slots behind the confirmation panel), `building`, `needs_review`,
 * `failed`, and a simulated `ready` frame. Open review work is marked on its
 * page card; selecting the badge projects one question into the AgentBar.
 *
 * G1 (rep-observed gap): the rail used to mount only once `phase ===
 * 'building'`, which the server doesn't set until the model's first tool
 * call lands — several seconds after the rep's own Build click, during
 * which this view rendered an empty page grid and nothing else. `buildRequested`
 * (the client's own "the rep clicked Build" flag, from ProposalView) closes
 * that gap: the tracker now mounts immediately, showing a fixed "Starting
 * the build…" snapshot until the real `building` state arrives.
 */
export function LiveBuildView({
  state,
  proposal,
  buildRequested,
  onRetryBuild,
  onOpenDraft,
  onAttentionClick,
  details = [],
}: {
  state: ProposalBuildState;
  proposal: Proposal;
  buildRequested: boolean;
  onRetryBuild: () => void;
  onOpenDraft: () => void;
  onAttentionClick: (page: ProposalBuildPage) => void;
  details?: LivingBriefDetail[];
}): ReactElement {
  const showTracker = isBuildInFlight(state.phase, buildRequested);
  return (
    <div className="live-build-view">
      {showTracker ? (
        <StageTracker state={state.phase === 'building' ? state : startingStageTrackerState()} />
      ) : null}
      <PageGrid
        pages={state.pages}
        proposal={proposal}
        onAttentionClick={onAttentionClick}
        onRetryPage={onRetryBuild}
        details={details}
        reviewItems={state.reviewItems}
      />
      {state.phase === 'failed' ? (
        <div className="live-build-failed-bar">
          <p>{state.message ?? 'Your completed pages are still available.'}</p>
          <div className="live-build-failed-actions">
            <button type="button" className="live-build-failed-retry" onClick={onRetryBuild}>
              Retry build
            </button>
            <button type="button" className="live-build-failed-draft" onClick={onOpenDraft}>
              Open current draft
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
